// Relative imports on purpose: next.config.ts loads this module outside the
// tsconfig path alias.
import { INSURER_PAGE_SLUGS } from "./insurance/insurer-pages"
import {
  TEST_LOCALIZED_SLUGS,
  TEST_SLUG_REDIRECT_COUNTRIES,
} from "./generated/test-localized-slugs"

/**
 * Locale-specific URL slugs for pages whose route folder is English.
 *
 * The route folder stays English (one page, one `page.tsx`); the *public* URL
 * is translated per locale. So `/ch/de/insurance` is served — and linked,
 * canonicalised, sitemapped — as `/ch/de/versicherung`, while `/ch/en/insurance`
 * is unchanged.
 *
 * Everything below is derived from `LOCALIZED_SLUGS`, so adding a page is a
 * one-line change:
 *  - `localizedSlugRedirects()` — 308s the English URL → localized (next.config)
 *  - `localizedSectionRedirects()` — same, for a section's child paths
 *  - `localizedSlugRewrites()`  — serves the localized URL from the English route
 *  - `localizedPath()`          — internal links, canonical/hreflang, sitemap,
 *                                 search index, llms.txt
 *  - `canonicalSlugPath()`      — inverse, for anything that keys off the bare
 *                                 path (country gates, chat scope, md mirrors)
 *
 * Keys and values are bare paths (locale-stripped, leading slash). Matching is
 * exact — `/insurance-checker-terms` is untouched by the `/insurance` entry.
 * Entries listed in `LOCALIZED_SECTIONS` additionally translate their child
 * paths, so a section's listing and detail URLs stay in the same language.
 */
export const LOCALIZED_SLUGS: Record<string, Record<string, string>> = {
  "/insurance": { de: "/versicherung", fr: "/assurance" },
  // Per-insurer pages sit under the same translated segment:
  // `/versicherung/helsana`, `/assurance/helsana`. Matching is exact, so each
  // page needs its own entry — derived from the registry, one line per slug.
  // Empty while every page is behind its `released` gate.
  ...Object.fromEntries(
    INSURER_PAGE_SLUGS.map((slug) => [
      `/insurance/${slug}`,
      { de: `/versicherung/${slug}`, fr: `/assurance/${slug}` },
    ])
  ),
  // Membership page. FR only — DE/AT are German-only markets that advertise it
  // as coming-soon, and CH's de/en URLs keep the English segment. Exact match,
  // so `/membership-terms` and `/go/membership` are untouched.
  "/membership": { fr: "/abonnement" },
  // Biomarker glossary. FR only for now — the German and Dutch markets still
  // run the English segment (`/ch/de/biomarkers`, `/nl/nl/biomarkers`).
  "/biomarkers": { fr: "/biomarqueurs" },
  // Health-risk library. FR only for now — the German and Dutch markets still
  // run the English segment (`/ch/de/what-we-detect`, `/nl/nl/what-we-detect`).
  "/what-we-detect": { fr: "/pathologies-risques" },
  // Comparison guide. FR only for now — the German market keeps the English
  // segment (`/ch/de/compare-health-check-ups-switzerland`) and the page is
  // CH-gated, so no NL variant. Exact match; the route folder stays English.
  "/compare-health-check-ups-switzerland": {
    fr: "/comparatif-bilans-de-sante",
  },
  // Clinic finder. FR only for now — the German and Dutch markets still run
  // the English segment (`/ch/de/locations`, `/nl/nl/locations`).
  "/locations": { fr: "/centres" },
  // Test detail pages. Unlike the sections above, `/tests` itself stays
  // English in every market — only the leaf slug is translated, so these are
  // exact-match entries and `/tests` is deliberately NOT a LOCALIZED_SECTION.
  // Slugs are ASCII (no accents): `sanitizePathnameForHeader` in proxy.ts
  // rejects non-ASCII pathnames, and every canonical URL on the site is
  // ASCII-only.
  //
  // Only the three pages the generator does NOT emit are listed statically:
  // they have no product-backed localized `pathSlug` in the CMS (prostate-mri
  // and pelvic-mri are fr-only MRI add-on pages; advanced-blood-test's leaf
  // isn't translated), so without these lines they'd lose their French URL.
  // Every other /tests/* translation is CMS-sourced via the spread below —
  // listing them here too would be dead, shadowed duplication.
  "/tests/advanced-blood-test": { fr: "/tests/bilan-sanguin-avance" },
  "/tests/prostate-mri": { fr: "/tests/irm-prostate" },
  "/tests/pelvic-mri": { fr: "/tests/irm-pelvienne" },
  // CMS-sourced /tests/* entries (AHD-13374), spread in IMMEDIATELY AFTER the
  // static block above so it OVERRIDES any matching key — the CMS `pathSlug`
  // migration is live on prod, and `TEST_LOCALIZED_SLUGS` is populated (11
  // entries as of this writing: full-body-mri, calcium-score, body-composition,
  // brain-volumetry, the two hormone panels, omega + essential-b blood tests,
  // and the cortisol + psa add-on pages — all de+fr+nl except mamma-mri and
  // psa, which are narrower). See lib/generated/test-localized-slugs.ts and
  // scripts/generate-test-localized-slugs.ts (regenerate with
  // `pnpm run generate:test-slugs`). The de/nl entries' English→localized
  // redirect is additionally scoped to the markets that serve the page per
  // TEST_SLUG_REDIRECT_COUNTRIES below (AHD-13374, F1) — see
  // localizedSlugRedirects().
  ...TEST_LOCALIZED_SLUGS,
  // Free calculators. Unlike every entry above, these are translated in ALL
  // three non-English locales, because the tools exist in all three markets
  // the gate allows (CH + DE + NL, `page:tools*`) and a calculator is the one
  // surface a visitor reaches from a search engine by typing the tool's name
  // in their own language.
  //
  // Both halves of the path are translated — the section segment AND the leaf —
  // so a French reader never sees `/outils/bmi-calculator`. That takes one
  // exact-match entry per page rather than a `LOCALIZED_SECTIONS` opt-in: a
  // section only swaps its own segment (`/outils/bmi-calculator`), and every
  // child here is a static route folder, so they are enumerable. The trade-off
  // is that a seventh calculator needs its line below or it ships with an
  // English URL in three markets — asserted in
  // `__tests__/localized-route-slugs.test.ts` against `HEALTH_TOOLS`.
  //
  // German is one slug for three markets (`/ch/de`, `/de/de`, and `/at/de` if
  // the gate ever opens): this map is keyed by language, not by (country,
  // language). The leaves drop the redundant `-rechner` the section already
  // says. Dutch keeps `-calculator`, which is the Dutch term, not the English
  // one. All ASCII, no accents — `sanitizePathnameForHeader` (proxy.ts)
  // rejects non-ASCII pathnames.
  "/tools": { de: "/rechner", fr: "/outils", nl: "/rekentools" },
  "/tools/bmi-calculator": {
    de: "/rechner/bmi",
    fr: "/outils/calculateur-imc",
    nl: "/rekentools/bmi-calculator",
  },
  "/tools/bmr-calculator": {
    de: "/rechner/grundumsatz",
    fr: "/outils/calculateur-metabolisme-de-base",
    nl: "/rekentools/rustmetabolisme-calculator",
  },
  "/tools/score2-calculator": {
    de: "/rechner/score2",
    fr: "/outils/calculateur-score2",
    nl: "/rekentools/score2-calculator",
  },
  "/tools/fib4-calculator": {
    de: "/rechner/fib-4",
    fr: "/outils/calculateur-fib-4",
    nl: "/rekentools/fib-4-calculator",
  },
  "/tools/smaf5-calculator": {
    de: "/rechner/smaf-5",
    fr: "/outils/calculateur-smaf-5",
    nl: "/rekentools/smaf-5-calculator",
  },
  "/tools/mafld-calculator": {
    de: "/rechner/mafld",
    fr: "/outils/calculateur-mafld",
    nl: "/rekentools/mafld-calculator",
  },
}

/**
 * Entries whose CHILD paths are translated along with the parent:
 * `/biomarkers/ferritin` → `/biomarqueurs/ferritin`.
 *
 * A section is one route folder serving a listing *and* N detail pages, so
 * translating only the listing would split the tree — `/ch/fr/biomarqueurs`
 * linking to `/ch/fr/biomarkers/ferritine`. Opt a page in here and the whole
 * sub-tree moves; leave it out (the default) and matching stays exact, which is
 * what keeps `/insurance-checker-terms` clear of the `/insurance` entry.
 *
 * Only the section segment is translated. The slug after it is whatever the
 * route already produces — biomarker and disease slugs are localized in the CMS
 * (`lib/cms-localized-slugs.ts`), while filter slugs like `/biomarkers/blood`
 * and `/what-we-detect/core` are package ids and stay English in every locale.
 * City and clinic slugs under `/locations` are likewise untranslated: they are
 * proper nouns from the CMS (`/centres/zurich/z-point`), not prose.
 */
export const LOCALIZED_SECTIONS: readonly string[] = [
  "/biomarkers",
  "/what-we-detect",
  "/locations",
]

/**
 * Countries whose market actually serves a localized page. The English→localized
 * 308 is emitted only for these, because a redirect is only useful if its
 * destination exists.
 *
 * `/insurance` is CH-only (`page:insurance`) but the rule was emitted for every
 * country, so `/de/de/insurance` 308'd to `/de/de/versicherung` — which 404s.
 * Redirecting into a dead page is worse than the 404 it replaces: it costs a
 * hop, and it hides the real status from crawlers and from the 404 buckets we
 * triage in Sentry.
 *
 * Duplicated from `COUNTRY_GATES` deliberately: `next.config.ts` loads this
 * module outside the tsconfig path aliases (see the import at the top), so it
 * cannot reach `lib/country-gates.ts`, which imports `@/i18n/config`.
 * `__tests__/localized-route-slugs.test.ts` asserts the two agree — the same
 * guarded duplication `EXTRA_STATIC_SITEMAP_PATHS` uses for the sitemap.
 */
export const SLUG_COUNTRIES: Record<string, readonly string[]> = {
  "/insurance": ["ch"],
  ...Object.fromEntries(
    INSURER_PAGE_SLUGS.map((slug) => [`/insurance/${slug}`, ["ch"]])
  ),
}

/**
 * The `:country` matcher for a redirect source — narrowed to a path's own
 * markets where it has them, so the rule can't fire in a country that 404s.
 */
function countrySource(bare: string): string {
  const countries = SLUG_COUNTRIES[bare]
  return countries ? `:country(${countries.join("|")})` : ":country"
}

/** Localized bare path → canonical (English) bare path. */
const CANONICAL_BY_LOCALIZED: Record<string, string> = Object.fromEntries(
  Object.entries(LOCALIZED_SLUGS).flatMap(([canonical, byLocale]) =>
    Object.values(byLocale).map((localized) => [localized, canonical])
  )
)

/** Split `/insurance?x=1#y` into its path and everything after it. */
function splitPath(path: string): [string, string] {
  const i = path.search(/[?#]/)
  return i === -1 ? [path, ""] : [path.slice(0, i), path.slice(i)]
}

/**
 * Section prefix swap, canonical → `locale`: `/biomarkers/ferritin` becomes
 * `/biomarqueurs/ferritin`. Undefined when `withSlash` is not under a section,
 * or the section has no slug for this locale.
 */
function localizedSectionPath(
  withSlash: string,
  locale: string
): string | undefined {
  for (const section of LOCALIZED_SECTIONS) {
    if (!withSlash.startsWith(`${section}/`)) continue
    const localized = LOCALIZED_SLUGS[section]?.[locale]
    return localized ? localized + withSlash.slice(section.length) : undefined
  }
  return undefined
}

/** Inverse of {@link localizedSectionPath}: `/biomarqueurs/x` → `/biomarkers/x`. */
function canonicalSectionPath(withSlash: string): string | undefined {
  for (const section of LOCALIZED_SECTIONS) {
    for (const localized of Object.values(LOCALIZED_SLUGS[section] ?? {})) {
      if (withSlash.startsWith(`${localized}/`)) {
        return section + withSlash.slice(localized.length)
      }
    }
  }
  return undefined
}

/**
 * The public URL path for `bare` in `locale`. Returns `bare` unchanged for
 * locales (and paths) with no localized slug. Tolerates a missing leading
 * slash — `urlFor()` in the search-index builder passes bare segments.
 */
export function localizedPath(bare: string, locale: string): string {
  const [path, suffix] = splitPath(bare)
  const withSlash = path.startsWith("/") ? path : `/${path}`
  const localized =
    LOCALIZED_SLUGS[withSlash]?.[locale] ??
    localizedSectionPath(withSlash, locale)
  if (!localized) return bare
  return (path.startsWith("/") ? localized : localized.slice(1)) + suffix
}

/**
 * Inverse of `localizedPath`: the English route path a bare path resolves to.
 * Use it wherever code keys off the bare path of the *incoming* request, since
 * a visitor on `/ch/de/versicherung` must hit the same gate/scope/mirror as one
 * on `/ch/en/insurance`.
 */
export function canonicalSlugPath(bare: string): string {
  const [path, suffix] = splitPath(bare)
  const withSlash = path.startsWith("/") ? path : `/${path}`
  const canonical =
    CANONICAL_BY_LOCALIZED[withSlash] ?? canonicalSectionPath(withSlash)
  if (!canonical) return bare
  return (path.startsWith("/") ? canonical : canonical.slice(1)) + suffix
}

/**
 * Re-localize `bare` into `locale` no matter which locale it's currently in:
 * canonicalize back to the English route, then localize to the target. So
 * `/assurance` (fr) → `/versicherung` (de), and `/insurance` (en) → `/assurance`
 * (fr) with no intermediate redirect. This is what the locale switcher needs —
 * pasting the current literal slug after the new locale prefix lands on the
 * "Not found" page (a localized slug served under the wrong language) or costs
 * a 308 hop (English slug under a language that redirects it). Query/hash
 * preserved via `localizedPath`/`canonicalSlugPath`.
 */
export function relocalizePath(bare: string, locale: string): string {
  return localizedPath(canonicalSlugPath(bare), locale)
}

/**
 * Every translated form of a bare path, canonical excluded (empty for paths
 * that aren't translated). For call sites that match a path against a list of
 * route sub-paths and must accept both URLs — e.g. the dark-shell hero list.
 *
 * Exact only: callers pass route folders, not child paths, so a section's
 * children are out of scope here.
 */
export function localizedVariants(bare: string): string[] {
  const withSlash = bare.startsWith("/") ? bare : `/${bare}`
  const byLocale = LOCALIZED_SLUGS[withSlash]
  if (!byLocale) return []
  return Object.values(byLocale).map((p) =>
    bare.startsWith("/") ? p : p.slice(1)
  )
}

type Rule = { source: string; destination: string }

function forEachSlug(fn: (locale: string, en: string, loc: string) => void) {
  for (const [canonical, byLocale] of Object.entries(LOCALIZED_SLUGS)) {
    for (const [locale, localized] of Object.entries(byLocale)) {
      fn(locale, canonical, localized)
    }
  }
}

/** As {@link forEachSlug}, restricted to `LOCALIZED_SECTIONS` entries. */
function forEachSection(fn: (locale: string, en: string, loc: string) => void) {
  for (const section of LOCALIZED_SECTIONS) {
    for (const [locale, localized] of Object.entries(
      LOCALIZED_SLUGS[section] ?? {}
    )) {
      fn(locale, section, localized)
    }
  }
}

/**
 * The `:country` matcher for a `/tests/*` redirect, (canonical, locale)-
 * scoped: `TEST_SLUG_REDIRECT_COUNTRIES` (AHD-13374, F1) narrows it, for
 * the CMS-generated `de`/`nl` translations ONLY, to the markets that
 * structurally serve that language AND where the test itself is available
 * (`data/tests.ts`), so the English→localized 308 never lands on a market
 * that 404s the destination. `undefined` when this (path, locale) pair
 * isn't in the map at all — every non-/tests entry, `fr` (deliberately
 * left unscoped — see `scripts/generate-test-localized-slugs.ts`'s top
 * comment, F1 round 4), and the static fr-only /tests entries the
 * generator doesn't emit — meaning the caller falls back to
 * {@link countrySource}'s current behavior.
 */
function testSlugRedirectCountries(
  en: string,
  locale: string
): string[] | undefined {
  return TEST_SLUG_REDIRECT_COUNTRIES[en]?.[locale]
}

/**
 * Permanent redirects from the English URL to the localized one, for the page
 * and its `.md` mirror. Query strings ride along — Next.js forwards them when
 * the destination carries no query of its own.
 */
export function localizedSlugRedirects(): Array<Rule & { permanent: true }> {
  const rules: Array<Rule & { permanent: true }> = []
  forEachSlug((locale, en, loc) => {
    const scoped = testSlugRedirectCountries(en, locale)
    // Scoped to explicitly ZERO countries (every market the CMS test could
    // reach excludes it): no redirect rule at all — an unscoped `:country`
    // or an empty `:country()` would both be wrong.
    if (scoped && scoped.length === 0) return
    const source = scoped ? `:country(${scoped.join("|")})` : countrySource(en)
    for (const ext of ["", ".md"]) {
      rules.push({
        // Source narrows to the markets that serve the destination — either
        // the CMS/availability-derived list above, or the static
        // SLUG_COUNTRIES-based countrySource() fallback; the destination
        // keeps the bare `:country` placeholder Next.js interpolates the
        // captured segment into.
        source: `/${source}/${locale}${en}${ext}`,
        destination: `/:country/${locale}${loc}${ext}`,
        permanent: true,
      })
    }
  })
  return rules
}

/**
 * Permanent redirects for a localized section's CHILD paths:
 * `/ch/fr/biomarkers/ferritine` → `/ch/fr/biomarqueurs/ferritine`.
 *
 * Kept out of {@link localizedSlugRedirects} because of ordering: Next.js takes
 * the first matching redirect, and the legacy Framer slug fixups
 * (`/biomarkers/body-composition` → `/biomarkers/core`) have to resolve the slug
 * *before* the segment is translated — otherwise a stale FR link lands on
 * `/biomarqueurs/body-composition`, which is neither a biomarker nor a filter.
 * So next.config places this call after those rules; see the comment there.
 */
export function localizedSectionRedirects(): Array<Rule & { permanent: true }> {
  const rules: Array<Rule & { permanent: true }> = []
  forEachSection((locale, en, loc) => {
    for (const ext of ["", ".md"]) {
      // `:slug+` — one *or more* segments. A section can be deeper than one
      // level (`/locations/zurich/z-point`), and a single `:slug` silently
      // left those URLs un-redirected. `+` (not `*`) keeps the bare listing
      // out; `localizedSlugRedirects()` already owns that exact match.
      rules.push({
        source: `/:country/${locale}${en}/:slug+${ext}`,
        destination: `/:country/${locale}${loc}/:slug+${ext}`,
        permanent: true,
      })
    }
  })
  return rules
}

/**
 * `beforeFiles` rewrites serving the localized URL from the English route.
 * The `.md` mirror needs no rewrite — `lib/native-markdown/index.ts` matches
 * through `canonicalSlugPath()` instead, because proxy.ts has already turned
 * `/x.md` into `/markdown-mirror/...` by the time rewrites run.
 */
export function localizedSlugRewrites(): Rule[] {
  const rules: Rule[] = []
  forEachSlug((locale, en, loc) => {
    rules.push({
      source: `/:country/${locale}${loc}`,
      destination: `/:country/${locale}${en}`,
    })
  })
  // Section children, so the localized detail URL resolves to the English
  // route folder too. In practice proxy.ts has already rewritten these onto
  // the flag-permutation route (`/biomarkers/v/:code/:slug`) by the time
  // rewrites run — this is the path for anything that reaches Next without
  // passing through the middleware.
  forEachSection((locale, en, loc) => {
    rules.push({
      source: `/:country/${locale}${loc}/:slug+`,
      destination: `/:country/${locale}${en}/:slug+`,
    })
  })
  return rules
}
